/**
 * تخزين متصفّح آمن + معرّف فريد آمن.
 *
 * السببان الجذريّان لخطأين حقيقيّين على الإنتاج:
 *  1) `Failed to read the 'localStorage' property from 'Window': Access is denied`
 *     — مجرّد *قراءة* الخاصيّة ترمي داخل iframe/متصفّح محجوب التخزين،
 *     فيسقط أيّ إعداد يمرّرها مباشرةً (zustand persist مثلاً).
 *  2) `crypto.randomUUID is not a function` — غير متاح على Safari قديم
 *     ولا في سياق غير آمن.
 *
 * الحلّ: بديل في الذاكرة لنفس الواجهة، ومولّد معرّف بسلسلة هبوط.
 */

function createMemoryStorage(): Storage {
  const map = new Map<string, string>();
  return {
    get length() {
      return map.size;
    },
    clear: () => map.clear(),
    getItem: (key: string) => map.get(key) ?? null,
    key: (index: number) => Array.from(map.keys())[index] ?? null,
    removeItem: (key: string) => void map.delete(key),
    setItem: (key: string, value: string) => void map.set(key, String(value)),
  } as Storage;
}

const memory: { local?: Storage; session?: Storage } = {};
const wrappers = new WeakMap<Storage, Storage>();

function resilientStorage(store: Storage): Storage {
  const existing = wrappers.get(store);
  if (existing) return existing;
  const fallback = createMemoryStorage();
  let blocked = false;
  function read<T>(action: () => T, backup: () => T): T {
    if (!blocked) {
      try { return action(); } catch { blocked = true; }
    }
    return backup();
  }
  const wrapper: Storage = {
    get length() { return read(() => store.length, () => fallback.length); },
    key: (index) => read(() => store.key(index), () => fallback.key(index)),
    getItem: (key) => read(() => {
      const value = store.getItem(key);
      if (value === null) fallback.removeItem(key);
      else fallback.setItem(key, value);
      return value;
    }, () => fallback.getItem(key)),
    setItem: (key, value) => {
      // Preserve the newest cart even when a large write exceeds quota after
      // startup. Never read the stale disk value after switching to memory.
      fallback.setItem(key, value);
      read(() => store.setItem(key, value), () => undefined);
    },
    removeItem: (key) => {
      fallback.removeItem(key);
      read(() => store.removeItem(key), () => undefined);
    },
    clear: () => {
      fallback.clear();
      read(() => store.clear(), () => undefined);
    },
  };
  wrappers.set(store, wrapper);
  return wrapper;
}

/** يعيد التخزين الحقيقيّ إن كان متاحاً فعلاً، وإلّا بديلاً في الذاكرة. */
export function safeStorage(kind: "local" | "session"): Storage {
  try {
    if (typeof window !== "undefined") {
      const store = kind === "local" ? window.localStorage : window.sessionStorage;
      // Guard every operation, not just a small one-time probe.
      return resilientStorage(store);
    }
  } catch {
    /* التخزين محجوب → بديل الذاكرة */
  }
  return (memory[kind] ??= createMemoryStorage());
}

/** معرّف فريد يعمل على كلّ المتصفّحات (بلا اعتماد على crypto.randomUUID). */
export function safeUUID(): string {
  try {
    const c = globalThis.crypto;
    if (typeof c?.randomUUID === "function") return c.randomUUID();
    if (typeof c?.getRandomValues === "function") {
      const bytes = c.getRandomValues(new Uint8Array(16));
      bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
      bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
      const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }
  } catch {
    /* الهبوط أدناه */
  }
  const rand = () => Math.random().toString(16).slice(2, 10);
  return `${rand()}-${rand().slice(0, 4)}-4${rand().slice(0, 3)}-a${rand().slice(0, 3)}-${rand()}${rand().slice(0, 4)}`;
}
